/**
 * Frontend axios engine. The ONLY place the web app talks HTTP.
 *
 * What lives here:
 *   - Singleton axios instance preconfigured with the API base URL and
 *     `withCredentials: true` so the session cookie flows.
 *   - Request interceptor that stamps `x-csrf-token` on every non-GET when the
 *     token is set (rotated by login/refresh/me).
 *   - Response interceptor that performs ONE deduped `POST /auth/refresh` on
 *     401, then retries the original request transparently. Refresh itself is
 *     CSRF-exempt server-side so a cold reload before /me works.
 *   - Error mapping: any non-2xx is converted to an `APIError` carrying the
 *     server's application/problem+json envelope. `_client.ts` and
 *     `onboarding.ts` both rely on this so call-sites can `catch (e) { if (e
 *     instanceof APIError) ... }` regardless of the request shape.
 *
 * Each HTTP verb has its own function below so per-method behavior (extra
 * headers, default params, logging) can be customized in one place without
 * touching call-sites. To change something for every GET request app-wide,
 * edit `get()`; same for `post()`, `put()`, etc. Interceptors live on the
 * private axios instance so they fire regardless of which verb you call.
 *
 * Call surface:
 *   engine.get<T>(url, config?)
 *   engine.post<T>(url, data?, config?)
 *   engine.put<T>(url, data?, config?)
 *   engine.patch<T>(url, data?, config?)
 *   engine.delete<T>(url, config?)
 *   engine.request<T>({ method, url, params, data, headers, signal, ... })
 */
import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { apiBaseUrl, APIError, type ProblemDetail } from "./http.js";

let csrfToken: string | null = null;

/** Set the CSRF token (called by useAuth after login + refresh). */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

function isAuthBootstrapUrl(url: string): boolean {
  return url.includes("/api/v2/auth/refresh") || url.includes("/api/v2/auth/login");
}

/**
 * Transparent access-token refresh. The access JWT expires every ~15 min; the
 * session cookie lives for the full session. On a 401 we POST /auth/refresh
 * once (deduped across concurrent callers) to rotate the token, then the
 * caller retries the original request. Refresh is CSRF-exempt server-side so
 * it works even on a cold reload before /me has minted a token.
 */
let refreshPromise: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    const headers: Record<string, string> = { accept: "application/json" };
    if (csrfToken) headers["x-csrf-token"] = csrfToken;
    refreshPromise = axios
      .post<{ csrfToken?: string }>(`${apiBaseUrl}/api/v2/auth/refresh`, undefined, {
        headers,
        withCredentials: true,
        // The refresh call must not flow back through our 401 retry logic.
      })
      .then((r) => {
        if (r.data?.csrfToken) setCsrfToken(r.data.csrfToken);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

const instance = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  headers: { accept: "application/json" },
  // Throw on any non-2xx; the response interceptor converts to APIError.
  validateStatus: (s) => s >= 200 && s < 300,
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Stamp CSRF on every mutating request; rebuild fresh each call so retries
  // pick up a rotated token after refresh.
  const method = (config.method ?? "get").toUpperCase();
  if (method !== "GET" && csrfToken) {
    config.headers.set("x-csrf-token", csrfToken);
  }
  return config;
});

instance.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    // Mark the request once it has been retried after refresh so we don't
    // loop on repeated 401s.
    type Retryable = InternalAxiosRequestConfig & { _retried?: boolean };
    const original = err.config as Retryable | undefined;
    const status = err.response?.status;
    const url = original?.url ?? "";
    const isBootstrap = isAuthBootstrapUrl(url);

    if (status === 401 && original && !original._retried && !isBootstrap) {
      original._retried = true;
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return instance.request(original);
      }
    }

    // Convert to APIError for any error that carried a response. Network /
    // abort / timeout failures fall through as-is (callers handle them as
    // plain Error / AxiosError).
    if (err.response) {
      const fullUrl = (original?.baseURL ?? "") + (original?.url ?? "");
      const data = err.response.data;
      let problem: ProblemDetail;
      if (data && typeof data === "object") {
        problem = data as ProblemDetail;
      } else if (typeof data === "string" && data.length > 0) {
        problem = {
          status: err.response.status,
          title: err.response.statusText,
          detail: data,
        };
      } else {
        problem = { status: err.response.status, title: err.response.statusText };
      }
      return Promise.reject(new APIError(err.response.status, problem, fullUrl));
    }
    return Promise.reject(err);
  },
);

/** Fetch a resource. Add GET-wide behavior (cache busting, logging) here. */
function get<T = unknown>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return instance.get<T>(url, config);
}

/** Create / dispatch a resource. Add POST-wide behavior here. */
function post<T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return instance.post<T>(url, data, config);
}

/** Replace a resource. Add PUT-wide behavior here. */
function put<T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return instance.put<T>(url, data, config);
}

/** Partial update. Add PATCH-wide behavior here. */
function patch<T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return instance.patch<T>(url, data, config);
}

// `delete` is a reserved word; the function is defined under a free name and
// re-exported on the engine object below.
function del<T = unknown>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return instance.delete<T>(url, config);
}

/** Arbitrary request — the escape hatch for non-verb-shaped calls. */
function request<T = unknown>(
  config: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  return instance.request<T>(config);
}

export const engine = {
  get,
  post,
  put,
  patch,
  delete: del,
  request,
};

export type { AxiosRequestConfig, AxiosResponse };
